import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Upload, Image as ImageIcon, Trophy, Calendar, Users, DollarSign } from 'lucide-react';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorianEn from "react-date-object/locales/gregorian_en";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import Button from '../UI/Button';
import Input from '../UI/Input';
import { getFullImageUrl, uploadImage } from '../../utils/imageUpload';
import tournamentService from '../../services/tournamentService';
import gameService from '../../services/gameService';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  display: block;
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const InputField = styled(Input)`
  width: 100%;
`;

const TextArea = styled.textarea`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  
  &::placeholder {
    color: #6c6c8a;
  }
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
  
  option {
    background: #1a1a2e;
    color: #ffffff;
  }
`;

const ImageUploadContainer = styled.div`
  border: 2px dashed #2d2d44;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #00d4ff;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  color: white;
  font-size: 48px;
  opacity: 0.7;
`;

const UploadText = styled.div`
  color: #b8b8d1;
  font-size: 16px;
  margin-bottom: 10px;
`;

const UploadSubtext = styled.div`
  color: #6c6c8a;
  font-size: 14px;
`;

const FileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #4ecdc4;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  padding: 25px;
  border-top: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const TournamentModal = ({ 
  show, 
  tournament, 
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    picture: '',
    reservationDeadline: '',
    startDate: '',
    gameId: '',
    entryCost: 0,
    teamMembersCount: 0,
    teamsCount: 0,
    entryScore: 0,
    winnerPrize: 0,
    winnerScore: 0
  });
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingGames, setLoadingGames] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reservationDeadlineJalali, setReservationDeadlineJalali] = useState(null);
  const [startDateJalali, setStartDateJalali] = useState(null);

  useEffect(() => {
    if (show) {
      fetchGames();
      
      if (tournament) {
        setFormData({
          id: tournament.id || '',
          name: tournament.name || '',
          description: tournament.description || '',
          picture: tournament.picture || '',
          reservationDeadline: tournament.reservationDeadline ? new Date(tournament.reservationDeadline).toISOString() : '',
          startDate: tournament.startDate ? new Date(tournament.startDate).toISOString() : '',
          gameId: tournament.gameId || '',
          entryCost: tournament.entryCost || 0,
          teamMembersCount: tournament.teamMembersCount || 0,
          teamsCount: tournament.teamsCount || 0,
          entryScore: tournament.entryScore || 0,
          winnerPrize: tournament.winnerPrize || 0,
          winnerScore: tournament.winnerScore || 0
        });

        // Convert Gregorian dates to Persian for the date pickers
        if (tournament.reservationDeadline) {
          const gregorianDate = new DateObject({ date: tournament.reservationDeadline, calendar: gregorian, locale: gregorianEn });
          const persianDate = gregorianDate.convert(persian, persianFa);
          setReservationDeadlineJalali(persianDate);
        } else {
          setReservationDeadlineJalali(null);
        }

        if (tournament.startDate) {
          const gregorianDate = new DateObject({ date: tournament.startDate, calendar: gregorian, locale: gregorianEn });
          const persianDate = gregorianDate.convert(persian, persianFa);
          setStartDateJalali(persianDate);
        } else {
          setStartDateJalali(null);
        }
      } else {
        setFormData({
          name: '',
          description: '',
          picture: '',
          reservationDeadline: '',
          startDate: '',
          gameId: '',
          entryCost: 0,
          teamMembersCount: 0,
          teamsCount: 0,
          entryScore: 0,
          winnerPrize: 0,
          winnerScore: 0
        });
        setReservationDeadlineJalali(null);
        setStartDateJalali(null);
      }
      setError('');
      setSuccess('');
    }
  }, [show, tournament]);

  const fetchGames = async () => {
    try {
      setLoadingGames(true);
      const data = await gameService.getGames();
      setGames(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setLoadingGames(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      const path = await uploadImage(file, 'tournament');
      setFormData(prev => ({
        ...prev,
        picture: path
      }));
      setSuccess('تصویر با موفقیت آپلود شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  };

  const handleReservationDeadlineChange = (date) => {
    setReservationDeadlineJalali(date);
    if (date) {
      const gregorianDate = date.convert(gregorian, gregorianEn);
      const dateStringForBackend = gregorianDate.format("YYYY-MM-DDTHH:mm:ss");
      setFormData(prev => ({
        ...prev,
        reservationDeadline: dateStringForBackend
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        reservationDeadline: ''
      }));
    }
  };

  const handleStartDateChange = (date) => {
    setStartDateJalali(date);
    if (date) {
      const gregorianDate = date.convert(gregorian, gregorianEn);
      const dateStringForBackend = gregorianDate.format("YYYY-MM-DDTHH:mm:ss");
      setFormData(prev => ({
        ...prev,
        startDate: dateStringForBackend
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        startDate: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.gameId || !formData.startDate) {
      setError('لطفاً تمام فیلدهای الزامی را پر کنید');
      return;
    }

    if (!formData.picture) {
      setError('لطفاً تصویری برای تورنومنت آپلود کنید');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (tournament) {
        await tournamentService.updateTournament(formData);
        toast.success('تورنومنت با موفقیت ویرایش شد');
      } else {
        await tournamentService.createTournament(formData);
        toast.success('تورنومنت با موفقیت ایجاد شد');
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'خطا در ذخیره تورنومنت');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            {tournament ? 'ویرایش تورنومنت' : 'افزودن تورنومنت جدید'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <FormGroup fullWidth>
              <Label>نام تورنومنت *</Label>
              <InputField
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="نام تورنومنت را وارد کنید"
                required
              />
            </FormGroup>

            <FormGroup fullWidth>
              <Label>توضیحات</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="توضیحات تورنومنت را وارد کنید"
              />
            </FormGroup>

            <FormGroup>
              <Label>مهلت ثبت نام</Label>
              <DatePicker
                value={reservationDeadlineJalali}
                onChange={handleReservationDeadlineChange}
                calendar={persian}
                locale={persianFa}
                format="YYYY/MM/DD HH:mm:ss"
                inputClass="form-control"
                calendarPosition="bottom-right"
                plugins={[
                  <TimePicker key="time_picker_deadline" position="bottom" />
                ]}
                style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                  border: '1px solid #2d2d44',
                  borderRadius: '8px',
                  color: '#ffffff',
                  width: '100%',
                  padding: '12px 15px'
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>تاریخ شروع *</Label>
              <DatePicker
                value={startDateJalali}
                onChange={handleStartDateChange}
                calendar={persian}
                locale={persianFa}
                format="YYYY/MM/DD HH:mm:ss"
                inputClass="form-control"
                calendarPosition="bottom-right"
                plugins={[
                  <TimePicker key="time_picker_start" position="bottom" />
                ]}
                style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                  border: '1px solid #2d2d44',
                  borderRadius: '8px',
                  color: '#ffffff',
                  width: '100%',
                  padding: '12px 15px'
                }}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>بازی *</Label>
              <Select
                name="gameId"
                value={formData.gameId}
                onChange={handleChange}
                required
                disabled={loadingGames}
              >
                <option value="">
                  {loadingGames ? 'در حال بارگذاری...' : 'بازی را انتخاب کنید'}
                </option>
                {games.map(game => (
                  <option key={game.id} value={game.id}>
                    {game.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>تعداد بازیکنان در هر تیم *</Label>
              <InputField
                type="number"
                name="teamMembersCount"
                value={formData.teamMembersCount}
                onChange={handleChange}
                placeholder="تعداد بازیکنان"
                min="1"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>تعداد تیم‌ها *</Label>
              <InputField
                type="number"
                name="teamsCount"
                value={formData.teamsCount}
                onChange={handleChange}
                placeholder="تعداد تیم‌ها"
                min="1"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>هزینه ورود (تومان)</Label>
              <InputField
                type="number"
                name="entryCost"
                value={formData.entryCost}
                onChange={handleChange}
                placeholder="هزینه ورود"
                min="0"
              />
            </FormGroup>

            <FormGroup>
              <Label>امتیاز ورود</Label>
              <InputField
                type="number"
                name="entryScore"
                value={formData.entryScore}
                onChange={handleChange}
                placeholder="امتیاز ورود"
                min="0"
              />
            </FormGroup>

            <FormGroup>
              <Label>جایزه برنده (تومان)</Label>
              <InputField
                type="number"
                name="winnerPrize"
                value={formData.winnerPrize}
                onChange={handleChange}
                placeholder="جایزه برنده"
                min="0"
              />
            </FormGroup>

            <FormGroup>
              <Label>امتیاز برنده</Label>
              <InputField
                type="number"
                name="winnerScore"
                value={formData.winnerScore}
                onChange={handleChange}
                placeholder="امتیاز برنده"
                min="0"
              />
            </FormGroup>

            <FormGroup fullWidth>
              <Label>تصویر تورنومنت *</Label>
              <ImageUploadContainer onClick={() => document.getElementById('tournament-image-upload').click()}>
                {formData.picture ? (
                  <ImagePreview>
                    <img src={getFullImageUrl(formData.picture)} alt="Tournament preview" />
                  </ImagePreview>
                ) : (
                  <ImagePlaceholder>
                    <ImageIcon size={48} />
                  </ImagePlaceholder>
                )}
                <UploadText>
                  {uploading ? 'در حال آپلود...' : 'برای آپلود تصویر کلیک کنید'}
                </UploadText>
                <UploadSubtext>
                  فرمت‌های مجاز: JPG, PNG, GIF, WebP (حداکثر 5MB)
                </UploadSubtext>
                <FileInput
                  id="tournament-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </ImageUploadContainer>
            </FormGroup>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={loading}
            icon={<X size={16} />}
          >
            انصراف
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || uploading}
            icon={<Trophy size={16} />}
          >
            {loading ? 'در حال ذخیره...' : (tournament ? 'ویرایش تورنومنت' : 'ایجاد تورنومنت')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TournamentModal;